import React, { useEffect, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import PanelLayoutContent from "components/layout/PanelLayoutContent"
import { useNavigate } from "react-router-dom"
import { TslNewsletterService } from "services/api-workers-service"
import type { TslNewsletterEventSort } from "services/api-workers-service"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import Input from "ui/components/atoms/input"
import Select from "ui/components/atoms/select"
import Spinner from "ui/components/atoms/spinner"
import Switch from "ui/components/atoms/switch"
import Tag from "ui/components/atoms/tag"
import { Label, P } from "ui/components/atoms/typography"
import Alert from "ui/components/molecules/alert"
import { Pagination } from "ui/components/molecules/pagination"
import { formatDate } from "utils/date"

import { localeToFlag, priorityColor, priorityLabel, TSL_LANGUAGES } from "./data"
import TslNewsletterNav from "./TslNewsletterNav"

const PAGE_SIZE = 20

const SORT_OPTIONS: Array<[string, TslNewsletterEventSort]> = [
  ["Data wydarzenia ↑", "event_date"],
  ["Data wydarzenia ↓", "-event_date"],
  ["Priorytet ↑", "priority"],
  ["Priorytet ↓", "-priority"],
  ["Utworzono ↑", "created_at"],
  ["Utworzono ↓", "-created_at"],
]

const EventsList = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [language, setLanguage] = useState<string>("")
  const [minPriority, setMinPriority] = useState<string>("")
  const [dateFrom, setDateFrom] = useState<string>("")
  const [dateTo, setDateTo] = useState<string>("")
  const [upcomingOnly, setUpcomingOnly] = useState(false)
  const [sort, setSort] = useState<TslNewsletterEventSort>("event_date")

  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(0)
    }, 300)
  }, [search])

  const offset = page * PAGE_SIZE
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      "tsl",
      "events",
      { debouncedSearch, language, minPriority, dateFrom, dateTo, upcomingOnly, sort, offset },
    ],
    queryFn: () =>
      TslNewsletterService.listEvents({
        q: debouncedSearch || undefined,
        language: language || undefined,
        min_priority: minPriority ? Number(minPriority) : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        upcoming_only: upcomingOnly || undefined,
        sort,
        limit: PAGE_SIZE,
        offset,
      }),
  })

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE))

  const clearFilters = () => {
    setSearch("")
    setLanguage("")
    setMinPriority("")
    setDateFrom("")
    setDateTo("")
    setUpcomingOnly(false)
    setSort("event_date")
    setPage(0)
  }

  return (
    <PanelLayoutContent title="TSL Newsletter" text="Wydarzenia branżowe (targi, konferencje, deadliny)">
      <TslNewsletterNav />

      <FiltersWrapper $marginBottom="M">
        <Input
          name="q"
          label="Szukaj"
          placeholder="Tytuł wydarzenia"
          value={search}
          onChange={({ value }) => setSearch(value)}
          $width="240px"
        />
        <Select
          name="language"
          label="Język"
          options={[["Wszystkie", ""], ...TSL_LANGUAGES]}
          value={language}
          onChange={({ value }) => {
            setPage(0)
            setLanguage(value)
          }}
          $minWidth="180px"
        />
        <Select
          name="minPriority"
          label="Priorytet min."
          options={[
            ["Dowolny", ""],
            ["≥ 0 (Niska)", "0"],
            ["≥ 3 (Normalna)", "3"],
            ["≥ 6 (Wysoka)", "6"],
            ["≥ 9 (TOP)", "9"],
          ]}
          value={minPriority}
          onChange={({ value }) => {
            setPage(0)
            setMinPriority(value)
          }}
          $minWidth="180px"
        />
        <Input
          name="date_from"
          label="Od"
          value={dateFrom}
          onChange={({ value }) => {
            setPage(0)
            setDateFrom(value)
          }}
          inputParams={{ type: "date" }}
          $width="170px"
        />
        <Input
          name="date_to"
          label="Do"
          value={dateTo}
          onChange={({ value }) => {
            setPage(0)
            setDateTo(value)
          }}
          inputParams={{ type: "date" }}
          $width="170px"
        />
        <Select
          name="sort"
          label="Sortowanie"
          options={SORT_OPTIONS}
          value={sort}
          onChange={({ value }) => {
            setPage(0)
            setSort(value as TslNewsletterEventSort)
          }}
          $minWidth="200px"
        />
        <UpcomingSwitchWrapper>
          <Label $type="secondary" $marginBottom="XS">
            Tylko nadchodzące
          </Label>
          <Switch
            name="upcoming_only"
            value={upcomingOnly}
            onChange={({ value }) => {
              setPage(0)
              setUpcomingOnly(value)
            }}
          />
        </UpcomingSwitchWrapper>
        <Button $type="secondary" onClick={clearFilters}>
          Wyczyść filtry
        </Button>
      </FiltersWrapper>

      {isError && (
        <Alert $type="error" $marginBottom="S">
          Nie udało się pobrać listy wydarzeń. Sprawdź klucz API i połączenie.
        </Alert>
      )}

      {data && (
        <P>
          System znalazł <span>{data.total}</span> wydarzeń
        </P>
      )}

      {isLoading ? (
        <Row $justifyContent="center" $padding="M">
          <Spinner $size="30px" />
        </Row>
      ) : !data || data.items.length === 0 ? (
        <EmptyState>
          <P $marginBottom="S">Brak wydarzeń dla wybranych filtrów.</P>
          <Button $type="secondary" onClick={clearFilters}>
            Wyczyść filtry
          </Button>
        </EmptyState>
      ) : (
        <ListWrapper>
          {data.items.map(event => {
            const pc = priorityColor(event.priority)
            return (
              <EventRow key={event.id}>
                <Row $alignItems="flex-start" $justifyContent="space-between">
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Row $alignItems="center" $marginBottom="XXS">
                      <Tag
                        $type="secondary"
                        $showBorder={false}
                        $background={pc.bg}
                        $color={pc.color}
                        label={`P${event.priority ?? "?"} ${priorityLabel(event.priority)}`}
                        $marginRight="XS"
                      />
                      <Tag
                        $type="secondary"
                        $showBorder={false}
                        $background="background"
                        label={`${localeToFlag(event.language)} ${event.language ?? "—"}`}
                        $marginRight="XS"
                      />
                      <Label $marginBottom="0">{event.title || "(brak tytułu)"}</Label>
                    </Row>
                    <Row $alignItems="center" $marginBottom="XXS">
                      <P $small $marginBottom="0" $marginRight="S">
                        📅 {formatDate(event.event_date)}
                      </P>
                      <P $small $marginBottom="0" $marginRight="S">
                        Utworzono: {formatDate(event.created_at)}
                      </P>
                    </Row>
                  </Box>
                  {(event.article_id || event.article_url) && (
                    <ActionsCol>
                      {event.article_id && (
                        <Button
                          $type="secondary"
                          icon="copy"
                          onClick={() =>
                            navigate(`/panel/tsl-newsletter/articles/${event.article_id}`)
                          }
                        >
                          Artykuł
                        </Button>
                      )}
                      {event.article_url && (
                        <Button
                          $type="secondary"
                          icon="share"
                          onClick={() =>
                            window.open(event.article_url ?? "", "_blank", "noopener,noreferrer")
                          }
                        >
                          Źródło
                        </Button>
                      )}
                    </ActionsCol>
                  )}
                </Row>
              </EventRow>
            )
          })}
        </ListWrapper>
      )}

      {data && data.total > PAGE_SIZE && (
        <Pagination defaultPage={page + 1} totalPages={totalPages} onChange={e => setPage(e - 1)} />
      )}
    </PanelLayoutContent>
  )
}

export default EventsList

const FiltersWrapper = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  grid-gap: ${({ theme }) => theme.space.S};
`

const UpcomingSwitchWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
`

const ListWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  grid-gap: ${({ theme }) => theme.space.XS};
`

const EventRow = styled(Box)`
  padding: ${({ theme }) => theme.space.S};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  background: white;
`

const ActionsCol = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.XS};
  margin-left: ${({ theme }) => theme.space.S};
  flex-shrink: 0;
`

const EmptyState = styled(Box)`
  padding: ${({ theme }) => theme.space.M};
  text-align: center;
  background: ${({ theme }) => theme.color.background};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  margin-bottom: ${({ theme }) => theme.space.M};
`
